import { CustomEvents } from "../../config/CustomEvent";
import { PersistenceLevel } from "../../config/PersistenceLevels";
import Game from "../Game";
import { BaseGameEvents } from "./BaseGameEvents";
import {
  EventHandler,
  EventHandlerName,
  eventHandlerName,
} from "./EventHandler";
import { GameSprite } from "./GameSprite";
import { IoEvents } from "./IoEvents";
import { PhysicsEvents } from "./PhysicsEvents";
import { WithOwner } from "./WithOwner";

export type GameEvents = BaseGameEvents &
  PhysicsEvents &
  IoEvents &
  CustomEvents;

export type GameEventName = keyof GameEvents;
export type GameEventHandlerFn<Key extends GameEventName> = (
  data: GameEvents[Key],
) => void;
export type GameEventHandler<Key extends GameEventName> = Record<
  EventHandlerName<Key>,
  GameEventHandlerFn<Key>
>;

export function isGameEventHandler<T extends GameEventName>(eventName: T) {
  return (e: unknown): e is GameEventHandler<T> => {
    const handlerName = eventHandlerName(eventName);
    if (!(typeof e === "object" && e !== null)) {
      return false;
    }
    if (!(handlerName in e)) {
      return false;
    }
    if (typeof (e as any)[handlerName] !== "function") {
      return false;
    }
    return true;
  };
}

/**
 * An Entity represents any interactive game object that participates in the game loop.
 *
 * Entities are the fundamental building blocks of the game - everything from bullets to enemies
 * to UI managers extends from this interface. They follow an event-driven architecture where
 * they respond to game events like tick updates, collisions, and input.
 *
 * ## Entity Lifecycle
 * 1. **Creation**: Entity is constructed (may load from EntityDef)
 * 2. **Adding to Game**: Game.addEntity() is called, which:
 *    - Sets entity.game reference
 *    - Registers physics bodies/sprites with their respective systems
 *    - Sets up owner relationships (WithOwner pattern)
 *    - Calls onAdd() lifecycle method
 * 3. **Active Phase**: Entity receives events during game loop (onTick, onRender, etc.)
 * 4. **Destruction**: entity.destroy() is called, which:
 *    - Calls onDestroy() lifecycle method
 *    - Removes from game systems (physics, rendering)
 *    - Cleans up parent-child relationships
 *    - Sets entity.game to undefined
 *
 * ## Physics Integration
 * - **body**: Use for single physics body (most common case)
 * - **bodies**: Use for complex entities requiring multiple physics bodies
 * - All bodies automatically get owner property set to this entity
 * - Bodies are added/removed from physics world automatically
 *
 * ## Rendering Integration
 * - **sprite**: Use for single visual representation (most common case)
 * - **sprites**: Use for complex entities requiring multiple sprites/graphics
 * - All sprites automatically get owner property set to this entity
 * - Sprites are added/removed from renderer automatically
 *
 * ## Event System
 *
 * The entity system uses an event-driven architecture where entities communicate
 * through events rather than direct method calls. This provides loose coupling
 * and makes the system more maintainable.
 *
 * ### Event Handler Convention
 *
 * Events are handled by implementing methods with the pattern `on${EventName}`:
 * - Event "tick" → method "onTick"
 * - Event "beginContact" → method "onBeginContact"
 * - Event "scoreAdded" → method "onScoreAdded"
 *
 * The EventHandler type system automatically generates the correct method signatures
 * based on the event definitions.
 *
 * ### Core Game Events
 *
 * **Lifecycle Events** (BaseGameEvents):
 * - `onAdd()` - Called when entity is added to game
 * - `onTick(dt: number)` - Called every game tick (120 TPS)
 * - `onRender(dt: number)` - Called every render frame (variable rate)
 * - `onDestroy()` - Called when entity is being destroyed
 *
 * **Physics Events** (PhysicsEvents):
 * - `onBeginContact({other, bodyA, bodyB, shapeA, shapeB})` - Collision starts
 * - `onEndContact({other, bodyA, bodyB, shapeA, shapeB})` - Collision ends
 * - `onContacting({other, ...})` - Called every tick while colliding
 *
 * **Input Events** (IoEvents):
 * - `onKeyDown({key})` - Key press events
 * - `onKeyUp({key})` - Key release events
 * - `onMouseMove({position, delta})` - Mouse movement
 * - And many more input events...
 *
 * ### Custom Events
 *
 * Game-specific events are defined in `src/config/CustomEvent.ts`:
 * - `onScoreAdded({points, source})` - When score increases
 * - `onEnemyKilled({enemy, killer})` - When enemy dies
 * - `onLevelComplete({level})` - When level is finished
 *
 * ### Event Dispatching Patterns
 *
 * **From Entity** (most common):
 * ```typescript
 * // Dispatch from any entity
 * this.dispatch("enemyKilled", { enemy: this, killer: bullet });
 * ```
 *
 * **From Game** (for system events):
 * ```typescript
 * // Dispatch from game loop or systems
 * game.dispatch("levelComplete", { level: currentLevel });
 * ```
 *
 * **Pause Handling**:
 * ```typescript
 * // Respects pause state (default true)
 * this.dispatch("gameEvent", data, true);
 *
 * // Ignores pause state (for UI/system events)
 * this.dispatch("menuEvent", data, false);
 * ```
 *
 * ### Best Practices
 *
 * 1. **Use events for cross-entity communication** instead of direct references
 * 2. **Make event data immutable** - don't modify event objects
 * 3. **Keep event handlers fast** - avoid expensive operations
 * 4. **Use descriptive event names** - "enemyKilled" not "death"
 * 5. **Include relevant context** - source entity, affected entities, etc.
 * 6. **Handle event ordering** - events are processed in entity creation order
 *
 * ### Example Implementation
 *
 * ```typescript
 * class Enemy extends BaseEntity {
 *   health = 100;
 *
 *   onTick(dt: number) {
 *     // Regular update logic
 *     this.updateMovement(dt);
 *   }
 *
 *   onBeginContact({ other }: { other?: Entity }) {
 *     // Handle collision with bullet
 *     if (other?.tags.includes("bullet")) {
 *       this.health -= other.damage;
 *       if (this.health <= 0) {
 *         this.dispatch("enemyKilled", {
 *           enemy: this,
 *           killer: other,
 *           position: this.getPosition()
 *         });
 *         this.destroy();
 *       }
 *     }
 *   }
 *
 *   onScoreAdded({ points }: { points: number }) {
 *     // React to score changes (maybe show popup)
 *     if (points > 100) {
 *       this.showBonusEffect();
 *     }
 *   }
 * }
 * ```
 */
export default interface Entity extends EventHandler<GameEvents> {
  /** The game this entity belongs to. This should only be set by the Game. */
  game: Game | undefined;

  /**
   * Unique identifier for this entity. Optional but recommended for entities that need
   * to be looked up later via game.entities.getById(). Commonly used for singleton
   * managers like "scoreManager", "gameFlowManager", etc.
   *
   * @example
   * class ScoreManager extends BaseEntity {
   *   id = "scoreManager";
   * }
   *
   * // Later, access from anywhere:
   * const scoreManager = game.entities.getById("scoreManager");
   */
  id?: string;
  /** Tags to find entities by */
  readonly tags: ReadonlyArray<string>;

  /** Children that get added/destroyed along with this entity */
  readonly children?: Entity[];

  /** Entity that has this entity as a child */
  parent?: Entity;

  /**
   * Determines when this entity should be destroyed during game transitions.
   *
   * - **PersistenceLevel.Level**: Destroyed when level ends (most game objects)
   * - **PersistenceLevel.Game**: Destroyed when returning to main menu (UI, managers)
   * - **PersistenceLevel.Session**: Never automatically destroyed (global systems)
   *
   * The Game uses this during cleanup operations to determine which entities
   * to keep and which to remove when transitioning between game states.
   *
   * @example
   * class Bullet extends BaseEntity {
   *   persistenceLevel = PersistenceLevel.Level; // Dies at end of level
   * }
   *
   * class ScoreManager extends BaseEntity {
   *   persistenceLevel = PersistenceLevel.Game; // Survives between levels
   * }
   */
  readonly persistenceLevel: PersistenceLevel;

  /** True if this entity will stop updating when the game is paused. */
  readonly pausable: boolean;

  /** Called to remove this entity from the game */
  destroy(): void;

  ///////////////////////
  /// Rendering Stuff ///
  ///////////////////////

  /**
   * Single sprite for visual representation. Use this for entities with one visual element.
   * The Game automatically adds this to the renderer and sets sprite.owner = this entity.
   *
   * Choose sprite over sprites when you have a single visual representation.
   *
   * @example
   * class Bullet extends BaseEntity {
   *   constructor() {
   *     super();
   *     this.sprite = createGraphics("main")
   *       .moveTo(-6, 0)
   *       .lineTo(6, 0)
   *       .stroke({ color: 0xffff00, width: 1 });
   *   }
   * }
   */
  sprite?: GameSprite;

  /**
   * Multiple sprites for complex visual representation. Use this for entities that need
   * multiple visual elements (e.g., a turret with base + barrel, UI with multiple parts).
   * The Game automatically adds all sprites to the renderer and sets sprite.owner = this entity.
   *
   * Choose sprites over sprite when you need multiple visual elements.
   *
   * @example
   * class Turret extends BaseEntity {
   *   constructor() {
   *     super();
   *     const base = createGraphics("main").circle(0, 0, 20);
   *     const barrel = createGraphics("main").rect(-2, -30, 4, 30);
   *     this.sprites = [base, barrel];
   *   }
   * }
   */
  sprites?: GameSprite[];

  /////////////////////
  /// Physics Stuff ///
  /////////////////////

  /**
   * Single physics body for collision detection and physics simulation.
   * Use this for entities with one physics representation (most common case).
   *
   * The Game automatically:
   * - Adds body to physics world when entity is added
   * - Sets body.owner = this entity (enables collision event routing)
   * - Removes body from physics world when entity is destroyed
   *
   * Choose body over bodies for simple physics entities.
   *
   * @example
   * class Bullet extends BaseEntity {
   *   constructor() {
   *     super();
   *     this.body = new Body({ mass: 0.1 });
   *     const shape = new Circle({ radius: 2 });
   *     this.body.addShape(shape);
   *   }
   * }
   */
  readonly body?: p2.Body & WithOwner;

  /**
   * Multiple physics bodies for complex physics representation.
   * Use this for entities that need multiple physics bodies (rare - usually
   * composition with child entities is preferred).
   *
   * The Game automatically:
   * - Adds all bodies to physics world when entity is added
   * - Sets body.owner = this entity for each body
   * - Removes all bodies from physics world when entity is destroyed
   *
   * Choose bodies over body only when you need multiple separate physics bodies
   * that can't be achieved with multiple shapes on a single body.
   */
  readonly bodies?: readonly (p2.Body & WithOwner)[];

  /** Physics springs that gets automatically added/removed from the world */
  readonly springs?: p2.Spring[];

  /** Physics constraints that gets automatically added/removed from the world */
  readonly constraints?: p2.Constraint[];
}
